// Step 19 — executive-situation card.
//
// Compact "what is happening" summary the AI user sees at the top of
// the intelligence workspace. Every value is a `Fact` from the
// intelligence layer — if it's missing we say "Okänd" rather than
// invent a number.

import { AISection } from "../AIPrimitives";

function _fact(sections, section, field) {
  const f = sections?.[section]?.[field];
  return f && f.known ? f : null;
}

function _formatMoney(v, unit) {
  if (typeof v !== "number" || !Number.isFinite(v)) return String(v ?? "");
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1).replace(".0", "")} M${unit || "SEK"}`;
  if (abs >= 1_000) return `${(v / 1_000).toFixed(0)} k${unit || "SEK"}`;
  return `${v} ${unit || "SEK"}`;
}

export default function AIExecutiveSituation({ dealSnapshot, companySnapshot, communicationSummary }) {
  const dealSections = dealSnapshot?.sections || {};
  const companySections = companySnapshot?.sections || {};

  const targetName = _fact(companySections, "identity", "target_company_name");
  const stage = _fact(dealSections, "process", "deal_stage");
  const status = _fact(dealSections, "process", "deal_status");
  const revenue = _fact(companySections, "financials", "revenue");
  const fiscalYear = _fact(companySections, "financials", "fiscal_year");
  const eqMid = _fact(dealSections, "valuation", "equity_value_mid");
  const eqLow = _fact(dealSections, "valuation", "equity_value_low");
  const eqHigh = _fact(dealSections, "valuation", "equity_value_high");
  const preliminary = _fact(dealSections, "valuation", "preliminary");
  const buyerCount = communicationSummary?.buyer_count;
  const inboundTotal = communicationSummary?.inbound_total;
  const outboundSent = communicationSummary?.outbound_sent_total;

  const rows = [];
  if (targetName) rows.push(["Målbolag", targetName.value]);
  if (stage) rows.push(["Steg", stage.value]);
  if (status) rows.push(["Status", status.value]);
  if (revenue)
    rows.push([
      "Omsättning",
      _formatMoney(revenue.value, revenue.unit) +
        (fiscalYear ? ` · ${fiscalYear.value}` : ""),
    ]);
  if (eqMid)
    rows.push([
      "Värdering (mid)",
      _formatMoney(eqMid.value, eqMid.unit) +
        (preliminary?.value ? " · preliminär" : ""),
    ]);
  if (eqLow && eqHigh)
    rows.push([
      "Värderingsband",
      `${_formatMoney(eqLow.value, eqLow.unit)} – ${_formatMoney(eqHigh.value, eqHigh.unit)}`,
    ]);
  if (typeof buyerCount === "number")
    rows.push(["Köpare i portföljen", String(buyerCount)]);
  if (typeof outboundSent === "number")
    rows.push(["Utskick / svar", `${outboundSent} skickat / ${inboundTotal ?? 0} svar`]);

  return (
    <AISection title="Läget just nu">
      <dl className="qai-exec-situation">
        {rows.length === 0 && (
          <div className="qai-shell-empty-hint">
            Inga strukturerade fakta finns i underlaget för denna affär ännu.
          </div>
        )}
        {rows.map(([k, v]) => (
          <div key={k} className="qai-exec-row">
            <dt className="qai-exec-key">{k}</dt>
            <dd className="qai-exec-val">{v}</dd>
          </div>
        ))}
      </dl>
    </AISection>
  );
}
